import {
  WindowOrder,
  WindowOrderStatus,
  WindowOrderSummary,
} from '@cutr/constants/windows/windows';

import { ApiError, logError } from '@/api/backend';
import { PaginatedData } from '@/api/backend/agent';
import { useAuthStore } from '@/api/login';
import i18n, { i18nInit } from '@/i18n';
import { getTicker } from '@/theme';

const baseUrl = import.meta.env.VITE_API_BASE_URL;

export type GetOrdersParams = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  statuses?: WindowOrderStatus[];
  createdAfter?: string;
  createdBefore?: string;
  submittedAfter?: string;
  submittedBefore?: string;
};

const checkError = async (res: Response) => {
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));

    if (json.statusCode === 401) {
      localStorage.removeItem('cutlist-auth');
      window.location.href = `/admin?redirect=${encodeURIComponent(
        window.location.pathname
      )}`;
      return res;
    }

    throw new ApiError(json.message || res.statusText, res.status, json);
  }

  return res;
};

export const agentApi = {
  createBaseUrl(resource = '', basePath = '') {
    return new URL(resource, baseUrl + basePath);
  },
  createUrl(path = '') {
    const url = this.createBaseUrl(path, '/v2/windows/agent/');

    return url;
  },
  async getHeaders({ includeAuth = true } = {}): Promise<
    Record<string, string>
  > {
    const auth = useAuthStore.getState();

    const ticker = getTicker();
    const headers: Record<string, string> = {
      'X-Cutlist-Source': ticker,
      'Content-Type': 'application/json',
    };

    await i18nInit;
    if (i18n.resolvedLanguage) {
      headers['Accept-Language'] = i18n.resolvedLanguage;
    }

    if (includeAuth && auth.token) {
      headers['Authorization'] = `Bearer ${auth.token}`;
    }

    return headers;
  },

  async getAllOrders(
    params: GetOrdersParams
  ): Promise<PaginatedData<WindowOrderSummary>> {
    const urlParams = new URLSearchParams({
      pageIndex: params.pageIndex.toString(),
      pageSize: params.pageSize.toString(),
    });

    for (const k of [
      'search',
      'statuses',
      'createdAfter',
      'createdBefore',
      'submittedAfter',
      'submittedBefore',
    ]) {
      const key = k as keyof GetOrdersParams;
      if (params[key]) {
        urlParams.set(key, params[key] as string);
      }
    }

    const headers = await agentApi.getHeaders();
    return fetch(agentApi.createUrl(`orders?${urlParams.toString()}`), {
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data, totalPages, totalRows }) => ({
        rows: data.orders,
        totalPages,
        totalRows,
      }))
      .catch(logError);
  },
  refreshOrder: async (orderId: string): Promise<WindowOrder> => {
    const headers = await agentApi.getHeaders();
    return fetch(agentApi.createUrl(`orders/${orderId}/refresh`), {
      headers: { ...headers },
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  cancelOrder: async (orderId: string): Promise<WindowOrder> => {
    const headers = await agentApi.getHeaders();
    return fetch(agentApi.createUrl(`orders/${orderId}/cancel`), {
      headers: { ...headers },
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  sendQuote: async (orderId: string): Promise<WindowOrder> => {
    const headers = await agentApi.getHeaders();
    return fetch(agentApi.createUrl(`orders/${orderId}/send-quote`), {
      headers: { ...headers },
      credentials: 'include',
      method: 'POST',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  async updateAgentLocale() {
    const headers = await agentApi.getHeaders();
    fetch(agentApi.createUrl(`locale`), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .catch(logError);
  },
};
